// Embeds a "Seats" stepper on a PLATFORM product's own page, sourced from a different, linked
// Shopify product (via the product.metafields.custom.linked_seat_product reference set in
// sections/main-product.liquid's linked_seat_stepper block) -- customers configure their whole
// order (platform + seats) from one page instead of visiting the seat product separately. Paired
// with <combined-add-to-cart>, which reads selectedSeatVariantId/selectedSeatQuantity/
// selectedSeatSellingPlanId off this element at submit time.
//
// The input's value is the TOTAL seat count, including the one admin seat bundled with the
// platform -- not how many EXTRA seats are being purchased. additionalSeats() converts between
// the two: total 1 means "just the platform, nothing extra" (valid, and the minimum -- a firm
// always has at least its one admin seat), total 2 means 1 additional seat, etc. Tier matching,
// per-seat pricing, and the seat line item's quantity all operate on additionalSeats, since
// that's what the linked seat product's own variants/tiers are priced and capped in terms of.
//
// Pricing must reflect whichever selling plan is selected on the PLATFORM (e.g. $0 during a free
// month), for both the platform's own price AND the seat's -- but the platform and seat products
// have SEPARATE Shopify selling plan records even for "the same" conceptual offer, so a plan id
// selected on one product can't be looked up directly on the other. Both pricing data blobs
// (main-product.liquid) key each selling plan allocation by the plan's own NAME
// (lowercased/trimmed) instead -- this store's convention is to give paired plans on different
// products the exact same customer-facing name, so this class resolves the platform's current
// plan name, then looks up the SEAT product's own allocation under that same name, both for
// display and for the actual selling plan id submitted with the seat line at add-to-cart time.
// (An earlier version classified plans into buckets by substring-matching "1 month"/"2 month" in
// the name -- dropped after it silently collided two different plans into one bucket key.)
if (!customElements.get('linked-seat-stepper')) {
  customElements.define(
    'linked-seat-stepper',
    class LinkedSeatStepper extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.priceTarget = this.dataset.priceTarget ? document.getElementById(this.dataset.priceTarget) : null;
        this.totalTarget = this.dataset.totalTarget ? document.getElementById(this.dataset.totalTarget) : null;
        this.recurringTarget = this.dataset.recurringTarget ? document.getElementById(this.dataset.recurringTarget) : null;
        this.platformVariantId = this.dataset.platformVariantId;
        this.fallbackPlatformPrice = Number(this.dataset.platformPrice) || 0; // cents, same convention as seat-total.js
        this.currency = this.dataset.currency || 'USD';

        this.tiers = this.readTiers();
        this.activeTier = this.matchTier(this.additionalSeats());
        this.platformPricing = this.readPlatformPricing();

        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));

        this.watchSellingPlanSelection();
        this.update();
      }

      disconnectedCallback() {
        clearInterval(this.sellingPlanTimer);
      }

      readTiers() {
        const script = this.querySelector('[data-linked-seat-tiers]');
        if (!script) return [];
        try {
          const tiers = JSON.parse(script.textContent);
          return Array.isArray(tiers) ? tiers.slice().sort((a, b) => a.min - b.min) : [];
        } catch (error) {
          console.warn('Unable to read linked seat tier data', error);
          return [];
        }
      }

      readPlatformPricing() {
        const script = this.querySelector('[data-linked-platform-pricing]');
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.warn('Unable to read linked platform pricing data', error);
          return null;
        }
      }

      // Same field/widget assumptions as seat-total.js and combined-add-to-cart.js: the
      // subscription app writes [name="selling_plan"] at add-to-cart time, or (before that) the
      // live choice lives in its own subs_type_*/subs_interval controls. Searched globally, not
      // scoped to a <form>, since these platform pages have no product-form/buy-buttons markup.
      selectedSellingPlanId() {
        const directField = document.querySelector('[name="selling_plan"]');
        if (directField?.value) return String(directField.value);

        const purchaseType = document.querySelector('[name^="subs_type_"]:checked');
        if (purchaseType && purchaseType.dataset.type !== 'subscription') return '';

        return String(document.querySelector('[name="subs_interval"]')?.value || '');
      }

      // Polls because the subscription app mutates its own controls by assigning to value rather
      // than dispatching events -- there is nothing for a listener/MutationObserver to catch.
      watchSellingPlanSelection() {
        this.lastSellingPlanId = this.selectedSellingPlanId();
        this.sellingPlanTimer = setInterval(() => {
          const current = this.selectedSellingPlanId();
          if (current === this.lastSellingPlanId) return;
          this.lastSellingPlanId = current;
          this.update();
        }, 200);
      }

      // The platform's own allocation for whichever plan is currently selected -- carries price
      // (today), recurring_price (ongoing), and plan_key (the plan's own name, lowercased/
      // trimmed, used to find the SEAT product's matching plan by exact name). Null if no plan is
      // selected yet or the id isn't found (e.g. a one-time/no-plan purchase type), in which case
      // callers fall back to the raw variant price for both figures.
      currentPlatformAllocation() {
        const variant = this.platformPricing?.variants?.[this.platformVariantId];
        const planId = this.selectedSellingPlanId();
        return (planId && variant?.allocations?.[planId]) || null;
      }

      // The SEAT tier's allocation matching the platform's current plan (by "Billing period"
      // selector label -- see main-product.liquid). If there's exactly one selling plan on this
      // tier, use it regardless of whether its label matches: with only one option there's
      // nothing to disambiguate, and requiring an exact label match would depend on a naming
      // convention that doesn't hold everywhere -- e.g. LDA has a single plan per product since
      // there's no Standard alternative, and its seat plan happens to be labeled "monthly" while
      // the platform's matching plan is labeled "monthly, after 2 months free". Falls back to the
      // tier's flat everyday price (as both "today" and "ongoing") when there's no selected
      // platform plan, no matching/single plan on this tier, or no active tier at all (0
      // additional seats) -- a flat { price, recurring_price } shape either way so callers don't
      // need to special-case the fallback.
      currentSeatAllocation() {
        const flatPrice = this.activeTier ? this.activeTier.price : 0;
        if (!this.activeTier) return { price: flatPrice, recurring_price: flatPrice, id: null };

        const allocations = this.activeTier.allocations || {};
        const planKey = this.currentPlatformAllocation()?.plan_key;
        if (planKey && allocations[planKey]) return allocations[planKey];

        const allocationValues = Object.values(allocations);
        if (allocationValues.length === 1) return allocationValues[0];

        return { price: flatPrice, recurring_price: flatPrice, id: null };
      }

      // Total seats, as entered in the stepper -- includes the bundled admin seat.
      currentQuantity() {
        return Number.parseInt(this.input.value, 10) || 1;
      }

      // How many EXTRA seats beyond the platform's bundled one -- this, not currentQuantity(),
      // is what the linked seat product's tiers/pricing/cart line quantity are defined in terms
      // of. Never negative: a total of 1 (or less, e.g. a cleared field) means zero extra seats.
      additionalSeats() {
        return Math.max(0, this.currentQuantity() - 1);
      }

      matchTier(quantity) {
        if (quantity <= 0) return null;
        return (
          this.tiers.find((tier) => quantity >= tier.min && (tier.max == null || quantity <= tier.max)) ||
          this.activeTier ||
          this.tiers[0] ||
          null
        );
      }

      // Native stepUp()/stepDown() already respect min/max, but a manually TYPED value is never
      // clamped by the browser -- it just marks the field :invalid while leaving the out-of-range
      // value in place. Beyond the cap, self-serve isn't offered at all (sales-assisted only), so
      // this snaps back to the boundary rather than letting an over-cap quantity reach checkout.
      clampToBounds() {
        const min = Number.parseInt(this.input.min, 10);
        const max = Number.parseInt(this.input.max, 10);
        let value = this.currentQuantity();
        if (!Number.isNaN(min)) value = Math.max(min, value);
        if (!Number.isNaN(max)) value = Math.min(max, value);
        if (String(value) !== this.input.value) this.input.value = String(value);
      }

      onButtonClick(event) {
        event.preventDefault();
        const previous = this.input.value;
        if (event.target.name === 'plus') {
          this.input.stepUp();
        } else {
          this.input.stepDown();
        }
        this.clampToBounds();
        if (previous !== this.input.value) this.input.dispatchEvent(this.changeEvent);
      }

      onInputChange() {
        this.clampToBounds();
        this.update();
      }

      update() {
        const additionalSeats = this.additionalSeats();
        this.activeTier = this.matchTier(additionalSeats);

        const platformVariant = this.platformPricing?.variants?.[this.platformVariantId];
        const platformAllocation = this.currentPlatformAllocation();
        const platformToday = platformAllocation ? platformAllocation.price : platformVariant?.price ?? this.fallbackPlatformPrice;
        const platformOngoing = platformAllocation
          ? platformAllocation.recurring_price ?? platformAllocation.price
          : platformVariant?.price ?? this.fallbackPlatformPrice;

        const seatAllocation = this.currentSeatAllocation();
        const seatToday = seatAllocation.price;
        const seatOngoing = seatAllocation.recurring_price ?? seatAllocation.price;

        if (this.priceTarget) {
          this.priceTarget.textContent = additionalSeats > 0 ? `${this.formatMoney(seatOngoing)} / seat` : '';
        }

        const todayTotal = platformToday + seatToday * additionalSeats;
        const ongoingTotal = platformOngoing + seatOngoing * additionalSeats;

        if (this.totalTarget) {
          this.totalTarget.textContent = this.formatMoney(todayTotal);
        }
        if (this.recurringTarget) {
          if (ongoingTotal !== todayTotal) {
            this.recurringTarget.textContent = `Then ${this.formatMoney(ongoingTotal)}/month`;
            this.recurringTarget.hidden = false;
          } else {
            this.recurringTarget.textContent = '';
            this.recurringTarget.hidden = true;
          }
        }
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.currency,
        }).format(cents / 100);
      }

      // Read by <combined-add-to-cart> at submit time -- variant id/quantity are in terms of
      // ADDITIONAL seats, matching the linked seat product's own variants. null/0 means "no seats
      // added beyond the one bundled with the platform," i.e. the cart shouldn't get a seat line
      // at all. selectedSeatSellingPlanId is the SEAT product's OWN plan id for the current
      // bucket -- never the platform's plan id, which belongs to a different Shopify record.
      get selectedSeatVariantId() {
        const additionalSeats = this.additionalSeats();
        return additionalSeats > 0 && this.activeTier ? this.activeTier.id : null;
      }

      get selectedSeatQuantity() {
        return this.additionalSeats();
      }

      get selectedSeatSellingPlanId() {
        return this.currentSeatAllocation().id;
      }
    },
  );
}
